import axios from "@/plugins/axios";
import qs from "qs";
import utils from "@/stores/utils";

const state = {
    mode: "operator",
    section: null,

    bundles: [],
    bundles_count: 0,
    bundles_loading: false,
    bundles_loading_errors: null,
    bundles_saving: false,
    bundles_saving_errors: {},
    bundle_stats: {},

    bundles_filters: { 
        limit: 20, 
        ordering: 'name',
        search: '',
        active: null,
        product_type: null,
    },
    bundles_cancel_source: null,

    bundles_stats_loading: false,
    bundles_stats_loading_errors: null,
    bundles_stats_cancel_source: null,
};

const mutations = {
    updateSection(state, section) {
        state.section = section;
    },
    
    // Mutations pour les bundles
    updateBundles(state, bundles) {
        state.bundles = bundles;
        state.bundles_loading = false;
        state.bundles_loading_errors = null;
    },

    updateBundlesCount(state, count) {
        state.bundles_count = count;
    },

    updateBundlesLoading(state, loading) {
        state.bundles_loading = loading;
    },

    updateBundlesLoadingErrors(state, errors) {
        state.bundles_loading_errors = errors;
    },

    updateBundlesCancelSource(state, source) {
        state.bundles_cancel_source = source;
    },

    updateBundlesFilters(state, bundles_filters) {
        state.bundles_filters = Object.assign(
            {
                limit: state.bundles_filters.limit || 20,
                ordering: state.bundles_filters.ordering || 'name',
            },
            bundles_filters
        );
    },

    updateBundlesSaving(state, saving) {
        state.bundles_saving = saving;
        if (saving) {
            state.bundles_saving_errors = {};
        }
    },

    updateBundlesSavingErrors(state, errors) {
        state.bundles_saving_errors = errors;
        state.bundles_saving = false;
    },

    updateBundleStats(state, stats) {
        state.bundle_stats = stats;
    },

    updateBundlesStatsLoading(state, loading) {
        state.bundles_stats_loading = loading;
    },

    updateBundlesStatsLoadingErrors(state, errors) {
        state.bundles_stats_loading_errors = errors;
    },

    updateBundlesStatsCancelSource(state, source) {
        state.bundles_stats_cancel_source = source;
    },

    updateFilters(state, filters) {
        state.bundles_filters = filters;
    },
};

const actions = {
    updateSection({ commit }, section) {
        commit('updateSection', section);
    },

    async init({ commit, dispatch }, params = {}) {
        // Initialiser les données par défaut
        commit('updateBundles', []);
        commit('updateBundlesCount', 0);
        commit('updateBundlesLoadingErrors', null);
        commit('updateBundlesSavingErrors', {});
        
        // Charger les bundles et les statistiques
        await Promise.all([
            dispatch('fetchBundles'),
            dispatch('fetchStats')
        ]);
    },

    // Actions pour les bundles
    async fetchBundles({ commit, state, dispatch }, params = {}) {
        commit('updateBundlesLoading', true);
        commit('updateBundlesLoadingErrors', null);

        if (state.bundles_cancel_source) {
            state.bundles_cancel_source.cancel("canceled");
        }

        const cancelSource = axios.CancelToken.source();
        commit('updateBundlesCancelSource', cancelSource);

        try {
            const filters = { ...state.bundles_filters, ...params };
            
            const response = await axios.get('/api/bundles/', {
                params: filters,
                cancelToken: state.bundles_cancel_source?.token
            });
            
            commit('updateBundlesLoading', false);
            commit('updateBundles', response.data.results);
            commit('updateBundlesCount', response.data.count);
            commit('updateBundlesCancelSource', null);
            
            return response;
        } catch (xhr_error) {
            if (axios.isCancel(xhr_error)) {
                return;
            }
            const error = utils.handleError(xhr_error);
            commit('updateBundlesLoadingErrors', error.details);
            commit('updateBundlesLoading', false);
            commit('updateBundlesCancelSource', null);
            throw error;
        } finally {
            dispatch('fetchStats');
        }
    },


    async deleteBundle({ commit, state, dispatch }, bundleId) {
        commit('updateBundlesSaving', true);
        try {
            await axios.post(`/api/bundles/${bundleId}/archive/`);
            // Supprimer le bundle de la liste
            const filteredBundles = state.bundles.filter(bundle => bundle.id !== bundleId);
            commit('updateBundles', filteredBundles);
            commit('updateBundlesCount', state.bundles_count - 1);
            dispatch('fetchBundles');
            dispatch('session/fetchStats', null, { root: true });
        } catch (error) {
            const errors = utils.handleError(error);
            commit('updateBundlesSavingErrors', errors);
            throw error;
        }
    },

    // Actions pour les filtres
    setFilters({ commit, dispatch }, filters) {
        commit('updateBundlesFilters', filters);
        dispatch('fetchBundles');
    },

    clearFilters({ commit, dispatch }) {
        commit('updateBundlesFilters', {
            search: '',
            active: null,
            product_type: null,
        });
        dispatch('fetchBundles');
    },

    // Actions pour la pagination
    setPage({ commit, dispatch, state }, page) {
        commit('updateBundlesFilters', { offset: (page - 1) * state.bundles_filters.limit });
        dispatch('fetchBundles');
    },

    // Actions pour rechercher
    searchBundles({ commit, dispatch }, searchTerm) {
        commit('updateBundlesFilters', { search: searchTerm, offset: 0 });
        dispatch('fetchBundles');
    },

    // Actions pour filtrer par statut
    filterByStatus({ commit, dispatch }, status) {
        commit('updateBundlesFilters', { active: status, offset: 0 });
        dispatch('fetchBundles');
    },

    // Actions pour les statistiques
    async fetchStats({ commit, state }) {
        commit('updateBundlesStatsLoading', true);

        if (state.bundles_stats_cancel_source) {
            state.bundles_stats_cancel_source.cancel("canceled");
        }

        const cancelSource = axios.CancelToken.source();
        commit('updateBundlesStatsCancelSource', cancelSource);

        try {
            const response = await axios.get('/api/bundles/stats/', {
                cancelToken: state.bundles_stats_cancel_source?.token
            });
            commit('updateBundleStats', response.data);
            commit('updateBundlesStatsCancelSource', null);
            return response.data;
        } catch (xhr_error) {
            if (axios.isCancel(xhr_error)) {
                return;
            }
            const error = utils.handleError(xhr_error);
            commit('updateBundlesStatsLoading', false);
            throw error;
        } finally {
            commit('updateBundlesStatsLoading', false);
        }
    },
};

const getters = {
    // Getters pour les bundles
    bundles: state => state.bundles,
    bundles_count: state => state.bundles_count,
    bundles_loading: state => state.bundles_loading,
    bundles_loading_errors: state => state.bundles_loading_errors,
    bundles_saving: state => state.bundles_saving,
    bundles_saving_errors: state => state.bundles_saving_errors,

    // Getters pour les filtres
    bundles_filters: state => state.bundles_filters,
    hasActiveFilters: state => {
        return state.bundles_filters.search || 
               state.bundles_filters.active !== null || 
               state.bundles_filters.product_type !== null;
    },

    // Getters pour la pagination
    currentPage: state => Math.floor((state.bundles_filters.offset || 0) / state.bundles_filters.limit) + 1,
    totalPages: state => Math.ceil(state.bundles_count / state.bundles_filters.limit),
    hasNextPage: state => (state.bundles_filters.offset || 0) + state.bundles_filters.limit < state.bundles_count,
    hasPreviousPage: state => (state.bundles_filters.offset || 0) > 0,

    // Getters pour les statistiques
    bundle_stats: state => state.bundle_stats,
    bundles_stats_loading: state => state.bundles_stats_loading,
    bundles_stats_loading_errors: state => state.bundles_stats_loading_errors,

    // Computed getters
    hasBundles: state => state.bundles.length > 0,
    hasStats: state => !!state.bundle_stats,
};

export default {
    namespaced: true,
    state,
    mutations,
    actions,
    getters,
};
